import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import TeamService from "services/TeamService";
import TimelineService from "services/TimelineService";
import { useTeamContext } from "store/contexts/teamContext";
import { useTimelineContext } from "store/contexts/timelineContext";
import { useNewPostContext } from "store/contexts/newPostContext";
import GeneralService from "services/GeneralService";
import { useDispatch, useSelector } from "react-redux";
import * as timelineActions from "reteamStore/actions";
import { useToasts } from "react-toast-notifications";
import { useCreatePostContext } from "store/contexts/createPostContext";
import LatestRecognized from "components/PeerRecognition/LatestRecognized/LatestRecognized";
import RecordVideo from "components/PeerRecognition/RecordVideo/RecordVideo";
import IntroVideos from "components/PeerRecognition/IntroVideos/IntroVideos";
import OutroVideos from "components/PeerRecognition/OutroVideos/OutroVideos";
import TextAreaSection from "components/PeerRecognition/TextAreaSection/TextAreaSection";
import ButtonWithProgress from "components/ButtonWithProgress";
import { useGlobalContext } from "store/contexts/globalContext";

import "assets/scss/partials/peer-recognition.scss";

export default () => {
  const dispatch = useDispatch();
  const { messageTemplates, introVideos, outroVideos } = useSelector(
    (state) => state.timelineReducer
  );
  const { handleClose } = useCreatePostContext();
  const { team } = useTeamContext();
  const { addToast } = useToasts();
  const { createPost } = useTimelineContext();
  const { postColors } = useNewPostContext();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMember, setSelectedMember] = useState([]);
  const [loading, setLoading] = useState(false);
  const { microsoftTeamsVideoText } = useGlobalContext();
  const [selectedTitleId, setSelectedTitleId] = useState(1);
  const [showMessages, setShowMessages] = useState(false);
  const [selectedColor, setSelectedColor] = useState(postColors[0]);
  const loaderStyle = {
    display: "flex",
    justifyContent: "center",
    height: "100%",
    alignItems: "center",
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        getTimeLineVideos("intro"),
        getTimeLineVideos("outro"),
      ]);
      setLoading(false);
    })();

    //eslint-disable-next-line
  }, []);

  const formik = useFormik({
    initialValues: {
      text: "",
      postattachment: null,
      intro_id: null,
      outro_id: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: Yup.object().shape(
      {
        text: Yup.string().when(["postattachment", "intro_id", "outro_id"], {
          is: (postattachment, intro_id, outro_id) => {
            return !postattachment && !intro_id && !outro_id;
          },
          then: Yup.string()
            .min(4, "Text must be from 4 to 5000 characters")
            .max(5000, "Text must be from 4 to 5000 characters")
            .required("Please enter the text or attach media"),
        }),
        postattachment: Yup.mixed().when(["text", "intro_id", "outro_id"], {
          is: (text, intro_id, outro_id) => {
            return !text && !intro_id && !outro_id;
          },
          then: Yup.mixed().required("Please enter the text or attach media"),
        }),
      },
      ["postattachment", "text"]
    ),
    onSubmit: async (values) => {
      try {
        let media = null;

        if (
          values?.text &&
          (values?.text.length < 4 || values?.text.length > 5000)
        )
          return setError("Text should be between 4 to 5000 characters");
        if (values.postattachment) {
          const preSignedResponse = await GeneralService.getPresignedUploadURL({
            filename: values.postattachment.name,
            mime_type: values.postattachment.type,
            file_size: values.postattachment.size,
            resource_type: "team",
            resource_id: team.id,
            resource_policy: "team.celebration",
          });

          const { key, url } = preSignedResponse.data.data;

          media = key;

          await GeneralService.uploadToS3(
            url,
            values.postattachment,
            (progress) => {
              setUploadProgress(`${Math.floor(progress)}%`);
            }
          );
        }
        let introOutroSelected = false;
        if (formik.values.intro_id || formik.values.outro_id) {
          introOutroSelected = true;
        }
        setUploadProgress(100);
        const response = await TeamService.createCelebration(team.id, {
          text: values.text,
          intro_video_id: formik.values.intro_id,
          outro_video_id: formik.values.outro_id,
          media,
          attachment: values.postattachment,
          introOutroSelected,
          tag_users:
            selectedMember.length !== 0
              ? selectedMember.map((selectedMember) => selectedMember.id)
              : null,
          color_id: selectedColor.id.toString(),
        });

        if (response.data.data) {
          response.data.data.type = "celebration";
        }

        if (values.postattachment) {
          var attachmentType = values.postattachment.type.split("/");
        }
        formik.resetForm();

        setUploadProgress(0);
        setSelectedMember([]);

        if (
          attachmentType &&
          attachmentType[0] === "video" &&
          attachmentType[1] !== "mp4" &&
          !microsoftTeamsVideoText
        ) {
          handleClose();
          addToast(
            "Your post is being processed. We’ll let you know when it’s ready to view. ",
            { appearance: "info" }
          );
          return;
        }
        createPost(response.data.data);
        handleClose();
      } catch (e) {
        console.log(e);
      }
    },
  });
  const setError = (value) => {
    formik.setErrors({ text: value });
  };

  const onAttachmentSelected = (file) => {
    formik.setFieldValue("postattachment", file);
  };

  const clearAttachment = () => {
    formik.setFieldValue("postattachment", null);
  };

  const toggleTitleBar = (value) => {
    setShowMessages(value);

    if (value === true) {
      var response = provideTitle(selectedTitleId);
      formik.setFieldValue(
        "text",
        response?.length > 0 ? response[0].title : null
      );
    }
  };

  const getTimeLineVideos = async (category) => {
    try {
      const response = await TimelineService.getTimeLineVideos(
        team.id,
        category
      );
      dispatch(timelineActions.getTimeLineVideos(response.data.data, category));
    } catch (error) {
      if (error.response) {
        console.log(error.response.data.message);
      }
    }
  };

  const selectTitle = (id, title) => {
    setSelectedTitleId(id);
    formik.setFieldValue("text", title);
  };

  const provideTitle = (id) => {
    return messageTemplates?.filter((message) => message.id === id);
  };

  const handleSelectVideo = (id, text) => {
    if (
      !formik.values.postattachment ||
      formik.values.postattachment?.type.includes("video")
    ) {
      if (formik.values[text]) {
        formik.setFieldValue([text], null);
        return;
      }
      formik.setFieldValue([text], id);
    } else {
      addToast(
        "Please Select Intro or Outro Video with Attached/Recorded Video",
        { appearance: "error" }
      );
    }
  };

  const setValue = (value) => {
    formik.setFieldValue("text", value);
  };

  return (
    <section style={{ width: "100%" }}>
      <form noValidate onSubmit={formik.handleSubmit}>
        <div className="peer-recognition">
          <div className="row">
            <div className="col-lg-3 col-md-3">
              <div className="row px-3 video-section">
                <IntroVideos
                  loading={loading}
                  introVideos={introVideos}
                  formik={formik}
                  loaderStyle={loaderStyle}
                  handleSelectVideo={handleSelectVideo}
                />
                <OutroVideos
                  loading={loading}
                  outroVideos={outroVideos}
                  formik={formik}
                  loaderStyle={loaderStyle}
                  handleSelectVideo={handleSelectVideo}
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <TextAreaSection
                setValue={setValue}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                selectedMember={selectedMember}
                setSelectedMember={setSelectedMember}
                showMessages={showMessages}
                toggleTitleBar={toggleTitleBar}
                formik={formik}
                selectTitle={selectTitle}
                selectedTitleId={selectedTitleId}
                postColors={postColors}
                onAttachmentSelected={onAttachmentSelected}
                team={team}
                uploadProgress={uploadProgress}
                addToast={addToast}
              />
            </div>
            <div className="col-lg-3 col-md-3 recordVideoContent">
              <div className="d-flex justify-content-center on-web">
                <p className="fw-bold title">
                  {formik.values.postattachment?.type.includes("image")
                    ? "Preview Attached Image"
                    : formik.values.postattachment?.type.includes("video/mp")
                    ? "Preview Attached Video"
                    : formik.values.postattachment?.type.includes("video/webm")
                    ? "Preview Recorded Video"
                    : "Record your video"}
                </p>
              </div>
              <RecordVideo
                formik={formik}
                onChange={onAttachmentSelected}
                recordedAttachment={formik.values.postattachment}
                clearRecordedMedia={clearAttachment}
              />
              <LatestRecognized />
            </div>
            <div className="on-mobile">
              <ButtonWithProgress
                initialText={"Create"}
                progress={uploadProgress}
                // onClick={formik.handleSubmit}
                buttonWithProgressClass="create-button"
                isSubmitting={formik.isSubmitting}
              />
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};
